import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Box } from '@mui/material';
import { ActionsBar } from '@/components/ActionsBar/ActionsBar';
import { useToolModeHotkeys } from '@/hooks/useToolModeHotkeys';
import {
  useCompositorView,
  useFlowState,
  useFlowView,
  useVideoState,
  useVideoView,
} from '@/components/Recipe/FlowContext';
import { getPercentageInt } from '@/utils/numbers';
import { TOOLBAR_WIDTH_V2 } from '@/components/Recipe/consts/ui';
import { FullMediaPlayerControls } from '@/components/MediaPlayerControls/MediaPlayerControls';
import { VideoView } from '@/components/Recipe/Views/VideoView';
import type { EvaluatedVideo, NodeId, VideoState } from 'web';

const ZOOM_STEP = 1.1;

export const DesignerEditorActionsBar = ({ nodeId }: { nodeId: NodeId }) => {
  const compositorView = useCompositorView(nodeId);
  const zoomLevel = useFlowState((state) => state.compositor[nodeId]?.zoomLevel) ?? 1;
  const toolMode = useFlowState((state) => state.compositor[nodeId]?.toolMode) ?? 'selection';
  const isSelectMode = toolMode === 'selection';

  const setZoomLevel = useCallback(
    (zoom: number) => {
      compositorView.setZoomLevel(zoom);
    },
    [compositorView],
  );

  const zoomIn = useCallback(() => {
    compositorView.setZoomLevel(compositorView.zoomLevel() * ZOOM_STEP);
  }, [compositorView]);

  const zoomOut = useCallback(() => {
    compositorView.setZoomLevel(compositorView.zoomLevel() / ZOOM_STEP);
  }, [compositorView]);

  const zoomToFit = useCallback(() => {
    compositorView.zoomToFit();
  }, [compositorView]);

  const setIsSelectMode = useCallback(
    (isSelectMode: boolean) => {
      compositorView.setToolMode(isSelectMode ? 'selection' : 'pan');
    },
    [compositorView],
  );

  const flowView = useFlowView();
  const handleUndoRedo = useCallback(
    (undoRedo: 'undo' | 'redo') => {
      if (undoRedo === 'undo') {
        flowView.undo();
      } else {
        flowView.redo();
      }
    },
    [flowView],
  );

  const toggleHandMode = useCallback(() => {
    if (compositorView.toolMode() === 'selection') {
      compositorView.setToolMode('pan');
    } else if (compositorView.toolMode() === 'pan') {
      compositorView.setToolMode('selection');
    }
  }, [compositorView]);

  useHotkeys('Space', toggleHandMode, { scopes: 'editor', enableOnFormTags: false });
  useToolModeHotkeys(setIsSelectMode, { scopes: 'editor' });

  const videoView = useVideoView(nodeId);
  const videoState = useVideoState(nodeId);
  const video = videoView.evaluateVideo();
  const shouldShowVideoControls = videoState !== undefined && video !== undefined;

  const positioningStyle = shouldShowVideoControls
    ? {}
    : ({
        position: 'absolute',
        bottom: '16px',
        left: `calc(50% - ${TOOLBAR_WIDTH_V2 / 2}px)`,
        transform: 'translateX(-50%)',
      } as const);
  return (
    <Box sx={positioningStyle}>
      <ActionsBar
        zoomPercentage={getPercentageInt(zoomLevel)}
        zoomLimitsPercentage={{ min: 1, max: 10000 }}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomToHundred={() => setZoomLevel(1)}
        onZoomToFit={zoomToFit}
        onUndoRedo={handleUndoRedo}
        canUndo={flowView.hasUndo()}
        canRedo={flowView.hasRedo()}
        isSelectMode={isSelectMode}
        setIsSelectMode={setIsSelectMode}
        isEditMode
        isFullMode={shouldShowVideoControls}
      >
        {shouldShowVideoControls && (
          <Box sx={{ flex: 1, paddingRight: '64px' }}>
            <VideoControlsWrapper videoState={videoState} video={video} videoView={videoView} />
          </Box>
        )}
      </ActionsBar>
    </Box>
  );
};

function VideoControlsWrapper({
  videoState,
  video,
  videoView,
}: {
  videoState: VideoState;
  video: EvaluatedVideo;
  videoView: VideoView;
}) {
  return (
    <FullMediaPlayerControls
      currentTime={videoState.currentTime}
      duration={video.duration}
      fps={video.fps}
      isPaused={videoState.paused}
      isMuted={videoState.muted}
      hasAudio={video.hasAudio ?? true}
      onTogglePlay={useCallback(() => {
        videoView?.togglePlay();
      }, [videoView])}
      onSetTime={useCallback(
        (time: number, ongoing: boolean) => {
          videoView?.setTime(time, ongoing);
        },
        [videoView],
      )}
      onToggleMute={useCallback(() => {
        videoView?.toggleMute();
      }, [videoView])}
    />
  );
}
